import { createContext, useContext, useState, type ReactNode } from "react";
import { DEFAULT_APP_ORDER_FEE } from "@/constants/status";
import { useToast } from "@/components/base/Toast";
import { branches as initialBranches } from "@/mocks/branches";
import { employees as initialEmployees } from "@/mocks/employees";
import { financialSummary, financialTransactions } from "@/mocks/financial";
import { menuCategories as initialCategories, menuProducts as initialProducts } from "@/mocks/menu";
import { notificationsList as initialNotifications } from "@/mocks/notifications";
import { orders as initialOrders } from "@/mocks/orders";
import type {
  AppNotification,
  BranchDetail,
  Employee,
  EmployeeStatus,
  FinancialTransaction,
  MenuCategory,
  MenuProduct,
  OrderRecord,
  OrderStatus,
  PaymentMethod,
  TaxInvoice,
} from "@/types";

const nowLabel = (): string => {
  const date = new Date();
  const day = new Intl.DateTimeFormat("ar-EG", {
    day: "numeric",
    month: "long",
  }).format(date);
  const time = new Intl.DateTimeFormat("ar-EG", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  return `${day} — ${time}`;
};

const ORDER_ACTIVITY_LABEL: Record<OrderStatus, string> = {
  new: "تم استلام الطلب",
  preparing: "بدء تحضير الطلب",
  almost: "الطلب قارب على الانتهاء",
  ready: "الطلب جاهز للاستلام",
  outForDelivery: "الطلب خرج للتوصيل",
  received: "تم استلام الطلب من العميل",
  missed: "انتهت مدة السماح دون استلام",
};

// Delivery orders log delivery wording instead of pickup wording.
const DELIVERY_ACTIVITY_LABEL: Partial<Record<OrderStatus, string>> = {
  ready: "الطلب جاهز للتوصيل",
  outForDelivery: "الطلب خرج للتوصيل",
  received: "تم تسليم الطلب",
};

interface StoreContextValue {
  products: MenuProduct[];
  categories: MenuCategory[];
  branches: BranchDetail[];
  employees: Employee[];
  orders: OrderRecord[];
  saveProduct: (product: MenuProduct) => void;
  deleteProduct: (id: string) => void;
  toggleProduct: (id: string, available: boolean) => void;
  saveCategory: (category: MenuCategory) => void;
  deleteCategory: (id: string) => void;
  toggleCategory: (id: string, active: boolean) => void;
  moveCategory: (id: string, direction: "up" | "down") => void;
  saveBranch: (branch: BranchDetail) => void;
  toggleBranchOpen: (id: string, open: boolean) => void;
  toggleBranchActive: (id: string, active: boolean) => void;
  saveEmployee: (employee: Employee) => void;
  setEmployeeStatus: (id: string, status: EmployeeStatus) => void;
  updateOrderStatus: (
    id: string,
    status: OrderStatus,
  ) => { recorded: boolean };
  appOrderFee: number;
  setAppOrderFee: (fee: number) => void;
  cashDebt: number;
  transactions: FinancialTransaction[];
  settleDebt: (amount: number, method: PaymentMethod) => number;
  notifications: AppNotification[];
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
  lastReceipt: TaxInvoice | null;
  setLastReceipt: (invoice: TaxInvoice | null) => void;
}

const StoreContext = createContext<StoreContextValue | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const { show } = useToast();
  const [products, setProducts] = useState<MenuProduct[]>(initialProducts);
  const [categories, setCategories] =
    useState<MenuCategory[]>(initialCategories);
  const [branches, setBranches] = useState<BranchDetail[]>(initialBranches);
  const [employees, setEmployees] = useState<Employee[]>(initialEmployees);
  const [orders, setOrders] = useState<OrderRecord[]>(initialOrders);
  const [appOrderFee, setAppOrderFeeState] = useState<number>(
    DEFAULT_APP_ORDER_FEE,
  );
  const [cashDebt, setCashDebt] = useState<number>(financialSummary.cashDebt);
  const [transactions, setTransactions] = useState<FinancialTransaction[]>(
    financialTransactions,
  );
  const [notifications, setNotifications] =
    useState<AppNotification[]>(initialNotifications);
  const [lastReceipt, setLastReceipt] = useState<TaxInvoice | null>(null);

  const saveProduct = (product: MenuProduct) => {
    setProducts((prev) =>
      prev.some((item) => item.id === product.id)
        ? prev.map((item) => (item.id === product.id ? product : item))
        : [product, ...prev],
    );
  };

  const deleteProduct = (id: string) => {
    setProducts((prev) => prev.filter((item) => item.id !== id));
  };

  const toggleProduct = (id: string, available: boolean) => {
    setProducts((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isAvailable: available } : item,
      ),
    );
  };

  const saveCategory = (category: MenuCategory) => {
    setCategories((prev) =>
      prev.some((item) => item.id === category.id)
        ? prev.map((item) => (item.id === category.id ? category : item))
        : [...prev, category],
    );
  };

  const deleteCategory = (id: string) => {
    setCategories((prev) => prev.filter((item) => item.id !== id));
  };

  const toggleCategory = (id: string, active: boolean) => {
    setCategories((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isActive: active } : item)),
    );
  };

  const moveCategory = (id: string, direction: "up" | "down") => {
    setCategories((prev) => {
      const index = prev.findIndex((item) => item.id === id);
      if (index === -1) return prev;
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const saveBranch = (branch: BranchDetail) => {
    setBranches((prev) =>
      prev.some((item) => item.id === branch.id)
        ? prev.map((item) => (item.id === branch.id ? branch : item))
        : [...prev, branch],
    );
  };

  const toggleBranchOpen = (id: string, open: boolean) => {
    setBranches((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isOpen: open } : item)),
    );
  };

  const toggleBranchActive = (id: string, active: boolean) => {
    setBranches((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, isActive: active, isOpen: active ? item.isOpen : false }
          : item,
      ),
    );
  };

  const saveEmployee = (employee: Employee) => {
    setEmployees((prev) =>
      prev.some((item) => item.id === employee.id)
        ? prev.map((item) => (item.id === employee.id ? employee : item))
        : [employee, ...prev],
    );
  };

  const setEmployeeStatus = (id: string, status: EmployeeStatus) => {
    setEmployees((prev) =>
      prev.map((item) => (item.id === id ? { ...item, status } : item)),
    );
  };

  const updateOrderStatus = (
    id: string,
    status: OrderStatus,
  ): { recorded: boolean } => {
    const target = orders.find((item) => item.id === id);
    if (!target) return { recorded: false };

    // App-fee dues for cash orders are recorded only for app orders paid in cash once preparing starts.
    const recorded =
      status === "preparing" &&
      target.source === "app" &&
      target.payment === "cash" &&
      !target.debtRecorded;

    const baseLabel =
      target.pickup === "delivery" && DELIVERY_ACTIVITY_LABEL[status]
        ? (DELIVERY_ACTIVITY_LABEL[status] as string)
        : ORDER_ACTIVITY_LABEL[status];

    const activityLabel = recorded
      ? "بدء تحضير الطلب — تم تسجيل رسوم التطبيق لطلبات الكاش"
      : baseLabel;

    setOrders((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status,
              timeAgo: "الآن",
              debtRecorded: item.debtRecorded || recorded,
              activity: [
                ...item.activity,
                { label: activityLabel, time: nowLabel() },
              ],
            }
          : item,
      ),
    );

    if (recorded) {
      const debtAmount = appOrderFee;
      setCashDebt((prev) => Math.round((prev + debtAmount) * 100) / 100);
      setTransactions((prev) => [
        {
          id: `TX-${Math.floor(9100 + Math.random() * 800)}`,
          date: nowLabel(),
          label: "رسوم التطبيق المستحقة لطلبات الكاش",
          type: "appFeeDebt",
          method: "cash",
          amount: -debtAmount,
          status: "due",
          orderId: target.id,
          branch: target.branch,
          reason: "رسوم تطبيق مستحقة عن طلب نقدي تم بدء تحضيره",
        },
        ...prev,
      ]);
    }

    // A delivery order that reaches «تم التسليم» pushes a dashboard reminder notification.
    const delivered =
      target.pickup === "delivery" &&
      status === "received" &&
      target.status !== "received";

    if (delivered) {
      setNotifications((prev) => [
        {
          id: `n-${target.id}-${Date.now()}`,
          type: "delivery",
          icon: "ri-e-bike-2-line",
          title: `تم تأكيد توصيل الطلب #${target.id}`,
          body: `أكّد المندوب تسليم الطلب #${target.id} للعميل ${target.customer} من ${target.branch} عبر «توصيل بواسطة المحل». تذكير: راجع حالة الطلب وأغلقه إن كان مكتملًا.`,
          time: "الآن",
          isRead: false,
          orderId: target.id,
        },
        ...prev,
      ]);

      // Shows a temporary dashboard notification for 10 seconds, then it disappears.
      show(
        `تم تأكيد تسليم الطلب #${target.id} للعميل ${target.customer}. راجع الطلب وأغلقه إن كان مكتملًا.`,
        "delivery",
        {
          duration: 10000,
          action: {
            label: `عرض الطلب #${target.id}`,
            to: `/orders/${target.id}`,
          },
        },
      );
    }

    return { recorded };
  };

  const markNotificationRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isRead: true } : item)),
    );
  };

  const markAllNotificationsRead = () => {
    setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
  };

  // Re-applies the configured fee to every order coming from the app.
  const setAppOrderFee = (fee: number) => {
    const value = Math.max(Math.round(fee * 100) / 100, 0);
    setAppOrderFeeState(value);
    setOrders((prev) =>
      prev.map((order) => {
        if (order.source !== "app") return order;
        const delta = value - order.appFee;
        return {
          ...order,
          appFee: value,
          amount: Math.round(Math.max(order.amount + delta, 0) * 100) / 100,
        };
      }),
    );
  };

  const settleDebt = (amount: number, method: PaymentMethod): number => {
    const nextDebt = Math.max(Math.round((cashDebt - amount) * 100) / 100, 0);
    setCashDebt(nextDebt);
    setTransactions((prev) => {
      const updated =
        nextDebt <= 0.001
          ? prev.map((item) =>
              item.type === "appFeeDebt" && item.status === "due"
                ? { ...item, status: "paid" as const }
                : item,
            )
          : prev;
      return [
        {
          id: `TX-${Math.floor(9100 + Math.random() * 800)}`,
          date: nowLabel(),
          label: "سداد رسوم التطبيق المستحقة لطلبات الكاش",
          type: "settlement",
          method,
          amount: -Math.round(amount * 100) / 100,
          status: "completed",
        },
        ...updated,
      ];
    });
    return nextDebt;
  };

  return (
    <StoreContext.Provider
      value={{
        products,
        categories,
        branches,
        employees,
        orders,
        saveProduct,
        deleteProduct,
        toggleProduct,
        saveCategory,
        deleteCategory,
        toggleCategory,
        moveCategory,
        saveBranch,
        toggleBranchOpen,
        toggleBranchActive,
        saveEmployee,
        setEmployeeStatus,
        updateOrderStatus,
        appOrderFee,
        setAppOrderFee,
        cashDebt,
        transactions,
        settleDebt,
        notifications,
        markNotificationRead,
        markAllNotificationsRead,
        lastReceipt,
        setLastReceipt,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error("useStore must be used within StoreProvider");
  }
  return context;
}